import { useState, FormEvent } from 'react';
import { saveLead } from '@/services/leads-site';


const urgencias = [
  { icon: 'ri-alarm-warning-line', text: 'Vagas limitadas por ciclo' },
  { icon: 'ri-calendar-close-line', text: 'Próxima turma com início imediato' },
  { icon: 'ri-money-dollar-circle-line', text: 'O custo de continuar desorganizado é maior que o investimento' },
];

export default function ProximosPassosSection() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const nome = form.querySelector('[name="nome"]')?.value || '';
    const email = form.querySelector('[name="email"]')?.value || '';
    const whatsapp = form.querySelector('[name="whatsapp"]')?.value || '';
    const empresa = form.querySelector('[name="empresa"]')?.value || '';
    const mensagem = form.querySelector('[name="mensagem"]')?.value || '';

    if (mensagem && mensagem.length > 500) {
      return;
    }

    setStatus('loading');

    try {
      const result = await saveLead({ nome, email, whatsapp, empresa, diagnostico: null });

      if (result.success) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        // Error shown via status
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="proximos-passos" className="py-24 md:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20business%20office%20Manaus%20Amazon%20Brazil%20aerial%20view%20city%20skyline%20at%20golden%20hour%20sunset%2C%20warm%20amber%20tones%2C%20dramatic%20sky%2C%20professional%20corporate%20atmosphere%2C%20wide%20panoramic%20shot%2C%20cinematic%20quality%2C%20deep%20rich%20colors&width=1440&height=800&seq=cta001&orientation=landscape"
          alt="Próximos passos"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gray-950/80" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-start">
          {/* Left */}
          <div>
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-amber-400 mb-4">
              Próximos passos
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-black text-white leading-tight mb-6">
              PRÓXIMA TURMA:<br />
              <span className="text-amber-400">VAGAS</span><br />
              LIMITADAS
            </h2>
            <p className="text-white/60 text-base md:text-lg leading-relaxed mb-8 md:mb-10 max-w-md">
              O custo de continuar desorganizado é maior que o investimento na transformação. Cada mês sem estrutura é dinheiro perdido.
            </p>

            <div className="space-y-3 md:space-y-4 mb-8 md:mb-10">
              {urgencias.map((u) => (
                <div key={u.text} className="flex items-center gap-3 md:gap-4">
                  <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-amber-400/10 border border-amber-400/20 rounded-xl shrink-0">
                    <i className={`${u.icon} text-amber-400 text-sm md:text-base`} />
                  </div>
                  <span className="text-white/80 text-sm">{u.text}</span>
                </div>
              ))}
            </div>

            {/* Antes vs Depois */}
            <div className="grid grid-cols-2 gap-3 md:gap-4 mb-8">
              <div className="bg-red-950/40 border border-red-800/30 rounded-2xl p-4 md:p-5">
                <p className="text-red-400 text-xs font-bold uppercase tracking-widest mb-3">Antes</p>
                <ul className="space-y-1.5 md:space-y-2">
                  {[
                    'Empresa desorganizada',
                    'Decisões no achismo',
                    'Vendas imprevisíveis',
                    'Sem controle financeiro',
                    'Excesso de trabalho do dono',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-1.5 md:gap-2 text-white/50 text-xs">
                      <i className="ri-close-line text-red-500 text-sm flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-emerald-950/40 border border-emerald-800/30 rounded-2xl p-4 md:p-5">
                <p className="text-emerald-400 text-xs font-bold uppercase tracking-widest mb-3">Depois</p>
                <ul className="space-y-1.5 md:space-y-2">
                  {[
                    'Gestão estruturada',
                    'Decisões baseadas em dados',
                    'Processo comercial organizado',
                    'Controle financeiro claro',
                    'Crescimento mais previsível',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-1.5 md:gap-2 text-white/70 text-xs">
                      <i className="ri-check-line text-emerald-400 text-sm flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Chamada final */}
            <div className="border border-amber-400/20 bg-amber-400/5 rounded-2xl p-5 md:p-6">
              <i className="ri-double-quotes-l text-amber-400 text-2xl mb-3 block" />
              <p className="text-white/80 text-sm md:text-base leading-relaxed italic">
                &ldquo;Se você quer parar de trabalhar no escuro e estruturar seu negócio de verdade, o momento de começar é agora.&rdquo;
              </p>
            </div>
          </div>

          {/* Right — Form */}
          <div className="bg-white rounded-3xl p-6 md:p-8 lg:p-10">
            {status === 'success' ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-6">
                  <i className="ri-check-double-line text-emerald-600 text-2xl" />
                </div>
                <h3 className="text-2xl font-black text-gray-900 mb-3">Solicitação Enviada!</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Recebemos seu interesse. Nossa equipe entrará em contato em breve para confirmar sua vaga.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-black text-gray-900 mb-2">Garantir Minha Vaga</h3>
                <p className="text-gray-400 text-sm mb-8">Preencha os dados abaixo e entraremos em contato.</p>

                <form
                  data-readdy-form
                  id="form-garantir-vaga"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Nome completo *
                    </label>
                    <input
                      type="text"
                      name="nome"
                      required
                      placeholder="Seu nome"
                      className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="seu@email.com"
                      className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      WhatsApp *
                    </label>
                    <input
                      type="tel"
                      name="whatsapp"
                      required
                      placeholder="(92) 9 0000-0000"
                      className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Nome da empresa *
                    </label>
                    <input
                      type="text"
                      name="empresa"
                      required
                      placeholder="Nome da sua empresa"
                      className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Segmento
                    </label>
                    <select
                      name="segmento"
                      className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-amber-400 transition-colors bg-white cursor-pointer"
                    >
                      <option value="">Selecione o segmento</option>
                      <option value="Comércio">Comércio</option>
                      <option value="Serviços">Serviços</option>
                      <option value="Logística">Logística</option>
                      <option value="Indústria">Indústria</option>
                      <option value="Outro">Outro</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                      Qual é o maior desafio da sua empresa?
                    </label>
                    <textarea
                      name="mensagem"
                      rows={3}
                      maxLength={500}
                      placeholder="Descreva brevemente..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:border-amber-400 transition-colors resize-none"
                    />
                    <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-red-500' : 'text-gray-400'}`}>
                      {charCount}/500
                    </p>
                  </div>

                  {status === 'error' && (
                    <p className="text-red-500 text-xs text-center">
                      Erro ao enviar. Tente novamente.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="whitespace-nowrap w-full bg-gray-900 hover:bg-gray-700 disabled:opacity-60 text-white font-bold text-sm py-4 rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2"
                  >
                    {status === 'loading' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        GARANTIR MINHA VAGA AGORA
                        <i className="ri-arrow-right-line" />
                      </>
                    )}
                  </button>

                  <p className="text-center text-gray-400 text-xs">
                    Vagas limitadas · Sem compromisso · Resposta em até 24h
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
